// src/components/HistoryTemplatesDrawer.tsx
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { AlignLeft, Clock, X } from 'lucide-react';
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Drawer,
  DrawerContent,
  DrawerTrigger,
  DrawerClose
} from "@/components/ui/drawer";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from "@/components/ui/tabs";
import { MessageHistoryTab } from "@/components/MessageHistoryTab";
import { TemplatesTab } from "@/components/TemplatesTab";

interface MessageHistoryItem {
  text: string;
  timestamp: Date;
}

interface SavedTemplate {
  id: string;
  title: string;
  content: string;
}

interface HistoryTemplatesDrawerProps {
  messageHistory: MessageHistoryItem[];
  templates: SavedTemplate[];
  currentInputValue: string;
  canSaveToServer: boolean;
  onApplyHistoryMessage: (message: string) => void;
  onApplyTemplate: (template: string) => void;
  onSaveTemplate: (title: string, content: string) => void;
  onDeleteTemplate: (templateId: string) => void;
  onDrawerOpenChange?: (open: boolean) => void;
  isDrawerOpen?: boolean;
}

export function HistoryTemplatesDrawer({
  messageHistory,
  templates,
  currentInputValue,
  canSaveToServer,
  onApplyHistoryMessage,
  onApplyTemplate,
  onSaveTemplate,
  onDeleteTemplate,
  onDrawerOpenChange,
  isDrawerOpen = false
}: HistoryTemplatesDrawerProps) {
  
  const handleApplyHistoryMessage = (message: string) => {
    onApplyHistoryMessage(message);
    onDrawerOpenChange?.(false);
  };

  const handleApplyTemplate = (template: string) => {
    onApplyTemplate(template);
    onDrawerOpenChange?.(false);
  };

  return (
    <Drawer 
      open={isDrawerOpen} 
      onOpenChange={onDrawerOpenChange}
    >
      <DrawerTrigger asChild>
        <Button 
          variant="outline" 
          size="icon" 
          className="h-10 w-10"
          title="History & Templates"
        >
          <AlignLeft className="h-4 w-4" />
        </Button>
      </DrawerTrigger>
      <DrawerContent className="px-0 pt-0 max-h-[80vh]">
        <div className="pt-4">
          <Tabs defaultValue="history" className="w-full">
            <TabsList className="w-full">
              <TabsTrigger value="history" className="flex-1">History</TabsTrigger>
              <TabsTrigger value="templates" className="flex-1">Templates</TabsTrigger>
            </TabsList>
            
            <TabsContent value="history" className="p-0">
              <MessageHistoryTab 
                messageHistory={messageHistory}
                onApplyHistoryMessage={handleApplyHistoryMessage}
              />
            </TabsContent>
            
            <TabsContent value="templates" className="p-0">
              <TemplatesTab 
                templates={templates}
                currentInputValue={currentInputValue}
                canSaveToServer={canSaveToServer}
                onApplyTemplate={handleApplyTemplate}
                onSaveTemplate={onSaveTemplate}
                onDeleteTemplate={onDeleteTemplate}
              />
            </TabsContent>
          </Tabs>
          
          <div className="p-4">
            <div className="text-xs text-gray-500 mb-3 text-center">
              Message History and Templates are saved locally to your browser storage
            </div>
            <div className="flex justify-end">
              <DrawerClose asChild>
                <Button variant="outline" size="sm">Close</Button>
              </DrawerClose>
            </div>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}